import React from "react";
import { compact } from "lodash";
import { useTranslation } from "react-i18next";

import { getAvailabilityState } from "../../../../commons/libs/availability";
import envelope from "../../../../commons/libs/externals/envelope";
import { formatDate, formatDateTime, formatUnit } from "../../../../commons/libs/formatters";
import { getFilterValuesForVariantsBySpecKey, getStandaloneProductVariants } from "../../../../commons/libs/specs";
import { getSpecConfig } from "../../../../commons/specs";
import { BicycleSpecKey, CustomBicycleFilterKey } from "../../../../commons/specs/bicycle";
import { ActiveFilters } from "../../../../commons/specs/filters";
import {
  Product,
  ProductId,
  ProductKey,
  ProductSpecKey,
  ProductType,
  SpecMap,
  StandaloneProductVariant
} from "../../../../commons/specs/product";
import { AssortmentType } from "../../../../commons/types/assortment";
import type { GetPrintItem } from "../../../../commons/types/print-mail";
import { SizingResult } from "../../../../commons/types/sizing";
import { IconMediumFrameSize } from "../../../../resources/icons";
import smartfitLogoInvertImage from "../../../../resources/images/body-sizing/smartfit-logo-invert.svg";
import smartfitLogoImage from "../../../../resources/images/body-sizing/smartfit-logo.svg";
import Availability from "../../../commons/components/Availability/Availability";
import AvailabilityLayout from "../../../commons/components/AvailabilityLayout/AvailabilityLayout";
import Table from "../../../commons/components/Table/Table";
import TableCell from "../../../commons/components/Table/TableCell";
import TableHeaderCell from "../../../commons/components/Table/TableHeaderCell";
import TableRow from "../../../commons/components/Table/TableRow";
import { ROUTES } from "../../../commons/routes";
import Button from "../../components/Button/Button";
import CenteredContent from "../../components/CenteredContent/CenteredContent";
import DescriptionList from "../../components/DescriptionList/DescriptionList";
import FlexLayout from "../../components/FlexLayout/FlexLayout";
import Icon from "../../components/Icon/Icon";
import Image from "../../components/Image/Image";
import Note from "../../components/Note/Note";
import Paragraph from "../../components/Paragraph/Paragraph";
import PriceInfo from "../../components/PriceInfo/PriceInfo";
import RadioField from "../../components/RadioField/RadioField";
import SelectCtaButton from "../../components/SelectField/SelectCtaButton";
import SelectField from "../../components/SelectField/SelectField";
import SelectMenuItem from "../../components/SelectField/SelectMenuItem";
import SizingStatusIcon from "../../components/SizingStatusIcon/SizingStatusIcon";
import Tooltip, { TooltipPosition } from "../../components/Tooltip/Tooltip";
import { clearActiveFilter, getActiveFilterValues } from "../../libs/filters";
import { useAppHistory } from "../../libs/hooks/use-app-routing";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import useOnMount from "../../libs/hooks/use-on-mount";
import useSpecFormatter from "../../libs/hooks/use-spec-formatter";
import useProduct from "../../libs/queries/use-product";
import { useSettings } from "../../libs/queries/use-settings";
import useVeloconnectProductAvailabilities from "../../libs/queries/use-veloconnect-product-availabilities";
import useWawiProductAvailabilities from "../../libs/queries/use-wawi-product-availabilities";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";
import StandaloneFilterModal from "../Modal/StandaloneFilterModal";
import DelayedLoadingIndicator from "../partials/DelayedLoadingIndicator";
import { OriginalPricePartial, PricePartial, SaleBadgePartial } from "../partials/PricePartials";
import { useTrackingContext } from "../utils/tracking-context";
import { useVeloconnectEndpointContext } from "../utils/veloconnect-endpoint-context";

import VariantInformationBoxPartial from "./VariantInformationBoxPartial";
import { useBodySizing, useLocalActiveFilters, useVariantCustomizationLink } from "./VariantsContentPartial.hooks";
import {
  getFilterSpecKeys,
  getVariantsTableSpecKeys,
  getVisibleVariantsTableSpecKeys
} from "./VariantsContentPartial.lib";

const LoadingResults = ({ isMobileBuild }: { isMobileBuild: boolean }) => {
  const { t } = useTranslation(["commons"]);

  return (
    <CenteredContent classNames={isMobileBuild ? ["u-padding-xxxl"] : ["u-padding-xxxxl", "u-padding-top-base"]}>
      <DelayedLoadingIndicator size="s" label={t("commons:variantsContentPartialProductDetails.loadingLabel")} />
    </CenteredContent>
  );
};

const EmptyResultsNote = ({
  isMobileBuild,
  isSmallViewport,
  onClick
}: {
  isMobileBuild: boolean;
  isSmallViewport: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation(["commons"]);

  return (
    <CenteredContent classNames={isMobileBuild ? ["u-padding-xxxl"] : ["u-padding-xxxxl", "u-padding-top-base"]}>
      <Note>
        <FlexLayout direction="column" gap="xs">
          <Paragraph kind="support" size={isSmallViewport ? "xs" : "base"}>
            {t("commons:variantsContentPartialProductDetails.table.emptyResults.noVariantsFoundExplanation")}
          </Paragraph>
          <Button block size="s" variant={isMobileBuild ? "accent" : "primary"} kind="solid" onClick={onClick}>
            {t("commons:variantsContentPartialProductDetails.table.emptyResults.resetFiltersButton")}
          </Button>
        </FlexLayout>
      </Note>
    </CenteredContent>
  );
};

interface Props<Type extends ProductType = ProductType> {
  variantId?: ProductId;
  product: Product<Type>;
  getPrintItem?: GetPrintItem;
  hasInternetConnectivity?: boolean;
  noSeparationShadow?: boolean;
  noTopSpacing?: boolean;
  openFrameSizeCalculatorTool?: () => void;
  onCloseBodySizingModal?: () => void;
}

const VariantsContentPartial = <Type extends ProductType = ProductType>({
  variantId,
  product,
  getPrintItem,
  onCloseBodySizingModal,
  openFrameSizeCalculatorTool,
  hasInternetConnectivity = false,
  noSeparationShadow = false,
  noTopSpacing = false
}: Props<Type>) => {
  const [dispatch, actions] = useCommonAppDispatcher();
  // Using unknown because commons can't import kiosk's BrowserLocationState (restricted imports)
  // BCD-8986 bc-desktop-client(fe): Move BrowserLocationState to shared types location
  const history = useAppHistory<unknown>();
  const {
    t,
    i18n: { language: locale }
  } = useTranslation(["commons"]);

  const settings = useSettings();

  const productId = product[ProductKey.ProductId];
  const productType = product[ProductKey.ProductType];
  const specConfig = getSpecConfig(productType);
  const specFormatter = useSpecFormatter<Type>("commons", productType, settings.initialized.customization);

  const veloconnectEndpoint = useVeloconnectEndpointContext();

  const breakpoints = useMediaQuery();

  const isMobileBuild = envelope.isMobileBuild;
  const isSmallViewport = !breakpoints.l;
  const isVariantCustomizationButtonEnabled = settings.initialized.features.variantCustomizationButtonEnabled;

  const selectedRowRef = React.useRef<HTMLTableRowElement>(null);
  const [selectFieldHasBeenOpened, setSelectFieldHasBeenOpened] = React.useState(false);

  const activeFilters = useCommonAppSelector(state => state.session.activeFilters as ActiveFilters<Type>);

  const localActiveFilters = useLocalActiveFilters({
    activeFilters,
    specConfig,
    settings: settings.initialized
  });

  const [activeBodySizingFilterValues] =
    getActiveFilterValues<ProductType.Bicycle, CustomBicycleFilterKey.Sizing>(
      activeFilters as ActiveFilters<ProductType.Bicycle>,
      CustomBicycleFilterKey.Sizing
    ) ?? [];
  const bodySizing = useBodySizing<Type>(product, activeFilters ?? {}, hasInternetConnectivity);

  const veloconnectProductAvailabilities = useVeloconnectProductAvailabilities(product);
  const wawiProductAvailabilities = useWawiProductAvailabilities(product);
  const variantCustomizationLink = useVariantCustomizationLink(veloconnectProductAvailabilities);

  // Mixpanel tracking for product variants view
  const { mixpanel } = useTrackingContext();
  useOnMount(() => {
    mixpanel?.trackProductVariantsOpened(product);
    return () => mixpanel?.trackProductVariantsClosed(product);
  });

  // We check both `query.error` and `isImportReady` because they cover different failure modes:
  // - `query.error` catches network/API failures when fetching the endpoint configuration
  // - `isImportReady` checks the endpoint's import state (has data and not currently running)
  const isVeloconnectAvailabilityReady =
    hasInternetConnectivity &&
    !!veloconnectEndpoint.query.data?.isConfigured &&
    !veloconnectEndpoint.query.error &&
    veloconnectEndpoint.isImportReady &&
    !veloconnectEndpoint.isImportOutdated;

  const isWawiAvailabilityReady =
    wawiProductAvailabilities.isActive &&
    (wawiProductAvailabilities.query.isFetching || wawiProductAvailabilities.hasAtLeastOneValidVariant);

  const showAvailabilityStatus = isVeloconnectAvailabilityReady || isWawiAvailabilityReady;

  const variantsTableSpecKeys = React.useMemo(
    () => getVariantsTableSpecKeys(product, specConfig, showAvailabilityStatus),
    [product, specConfig, showAvailabilityStatus]
  );
  const variantFilterSpecKeys = React.useMemo(
    () =>
      getFilterSpecKeys(product, specConfig, variantsTableSpecKeys, {
        bodySizingEnabled: settings.isBodySizingEnabled,
        hasInternetConnectivity,
        bikeFrameSizeCalculatorEnabled:
          settings.initialized.toolSettings.bikeFrameSizeCalculator.meta.toolVisible ?? false
      }),
    [
      product,
      specConfig,
      variantsTableSpecKeys,
      settings.isBodySizingEnabled,
      hasInternetConnectivity,
      settings.initialized.toolSettings.bikeFrameSizeCalculator.meta.toolVisible
    ]
  );

  const visibleVariantsTableSpecKeys = getVisibleVariantsTableSpecKeys(
    variantsTableSpecKeys,
    variantFilterSpecKeys,
    localActiveFilters.asProductFilters
  );

  const { query: productQuery } = useProduct<Type>({
    productId,
    filters: localActiveFilters.asProductFilters,
    options: {
      staleTime: 60 * 1000 * 5 // Keep the data fresh for the average session length of 5 minutes
    }
  });

  const allVariants = getStandaloneProductVariants(product);
  const filteredVariants = getStandaloneProductVariants(productQuery.data ?? []);

  const [selectedVariantId, setSelectedVariantId] = React.useState<ProductId>(
    () => variantId || allVariants[0][ProductKey.VariantId]
  );

  // Adjust selectedVariantId when the underlying data changes during render.
  // Starts as `undefined` so cached query data that is already present on mount is validated too.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevProductQueryData, setPrevProductQueryData] = React.useState<typeof productQuery.data>(undefined);
  if (prevProductQueryData !== productQuery.data) {
    setPrevProductQueryData(productQuery.data);
    // If the selected variant is filtered out, select the first non-filtered variant.
    if (
      filteredVariants.length > 0 &&
      filteredVariants.find(variant => variant[ProductKey.VariantId] === selectedVariantId) === undefined
    ) {
      setSelectedVariantId(filteredVariants[0][ProductKey.VariantId]);
    }
  }

  const [prevBodySizingResult, setPrevBodySizingResult] = React.useState(bodySizing.result);
  if (prevBodySizingResult !== bodySizing.result) {
    setPrevBodySizingResult(bodySizing.result);
    // If body sizing changes select the first variant which fits.
    if (bodySizing.result && filteredVariants.length > 0 && bodySizing.result[selectedVariantId] !== SizingResult.Fit) {
      const firstFittingVariantId = Object.entries(bodySizing.result).find(
        ([, result]) => result === SizingResult.Fit
      )?.[0];
      if (firstFittingVariantId !== undefined) {
        setSelectedVariantId(firstFittingVariantId);
      }
    }
  }

  const selectedVariant = filteredVariants.find(variant => variant[ProductKey.VariantId] === selectedVariantId);

  React.useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedVariantId, productQuery.isFetching]);

  const renderVariantFilters = () => {
    return variantFilterSpecKeys.map(specKey => {
      const isFrameSizeKey = specKey === BicycleSpecKey.FrameSize;
      const isSmartfitVariantFilter = isFrameSizeKey && bodySizing.isPossible && !!activeBodySizingFilterValues;

      const selectMenuItems = getFilterValuesForVariantsBySpecKey(filteredVariants, specKey)
        .map(item => ({
          ...item,
          formattedValue: specFormatter?.formatVariantValue(specKey, item.variant)
        }))
        .filter(({ formattedValue }) => formattedValue !== undefined)
        .map(({ value, variant }, index) => (
          <SelectMenuItem key={index} value={value} label={specFormatter?.formatVariantValue(specKey, variant)} />
        ));

      const handleResetSelection = () => {
        if (isSmartfitVariantFilter) {
          dispatch(
            actions.session.setActiveFilters(
              clearActiveFilter<ProductType.Bicycle, CustomBicycleFilterKey.Sizing>(
                (activeFilters as ActiveFilters<ProductType.Bicycle>) ?? {},
                CustomBicycleFilterKey.Sizing
              )
            )
          );
        } else {
          localActiveFilters.clearFilter(specKey);
        }
      };

      // TODO BCD-6031: type any because of types in SelectField
      const handleSelectFieldChange = (value: any) => {
        if (isFrameSizeKey && bodySizing.isPossible && value !== "smartfit") {
          dispatch(
            actions.session.setActiveFilters(
              clearActiveFilter<ProductType.Bicycle, CustomBicycleFilterKey.Sizing>(
                (activeFilters as ActiveFilters<ProductType.Bicycle>) ?? {},
                CustomBicycleFilterKey.Sizing
              )
            )
          );
        }

        localActiveFilters.toggleValue(specKey, value);
      };

      const renderFrameSizeFieldIcon = () => {
        if (activeBodySizingFilterValues) {
          const toolTipContent = (
            <>
              <DescriptionList>
                <dt>
                  {t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterActivatedTooltip.bodyHeight")}
                  :
                </dt>
                <dd>{formatUnit(activeBodySizingFilterValues.bodyHeight, locale, { unit: "centimeter" })}</dd>
                <dt>
                  {t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterActivatedTooltip.inseam")}:
                </dt>
                <dd>{formatUnit(activeBodySizingFilterValues.inseam, locale, { unit: "centimeter" })}</dd>
                <dt>
                  {t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterActivatedTooltip.armLength")}
                </dt>
                <dd>{formatUnit(activeBodySizingFilterValues.armLength, locale, { unit: "centimeter" })}</dd>
              </DescriptionList>
              <br />
              <img src={smartfitLogoImage} />
            </>
          );

          return (
            <Tooltip closeOnOutsideClick preferredPosition={TooltipPosition.Bottom} content={toolTipContent}>
              {({ open }) => (
                <span
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    open();
                  }}
                >
                  <Icon block source={IconMediumFrameSize} />
                </span>
              )}
            </Tooltip>
          );
        } else if (selectFieldHasBeenOpened) {
          return <Icon block source={IconMediumFrameSize} />;
        } else {
          return (
            <Tooltip
              openOnMountDelay={3000}
              openOnMount
              preferredPosition={TooltipPosition.Bottom}
              content={t("commons:variantsContentPartialProductDetails.filter.bodySizingEnabledFilterTooltip")}
            >
              <Icon block source={IconMediumFrameSize} />
            </Tooltip>
          );
        }
      };

      const renderCtaButton = (closePortal: () => void) => (
        <>
          {bodySizing.isPossible && (
            <SelectCtaButton
              key="bodySizingCta"
              disabled={!bodySizing.isAvailable}
              onClick={() => {
                dispatch(
                  actions.modal.showModal("bodySizing", { onClose: onCloseBodySizingModal, variant: "productDetail" })
                );
                handleResetSelection();
              }}
            >
              {t("commons:variantsContentPartialProductDetails.smartfitCtaButton")}
              <br />
              <img src={smartfitLogoInvertImage} />
            </SelectCtaButton>
          )}
          {!isMobileBuild &&
            openFrameSizeCalculatorTool &&
            settings.initialized.toolSettings.bikeFrameSizeCalculator.meta.toolVisible &&
            !bodySizing.isAvailable && (
              <SelectCtaButton
                key="frameSizeCalculatorCta"
                onClick={() => {
                  closePortal();
                  openFrameSizeCalculatorTool();
                }}
              >
                {t("commons:variantsContentPartialProductDetails.openFrameSizeCalculatorCtaButton")}
              </SelectCtaButton>
            )}
        </>
      );

      const selectFieldName = specFormatter?.formatLabel(specKey);
      const selectFieldValue = isSmartfitVariantFilter ? "smartfit" : localActiveFilters.getActiveFilterValue(specKey);

      const showUnknownSizingError = bodySizing.isPossible && !!bodySizing.error && !bodySizing.isLoading;
      const showNoFittingDataSizingError =
        bodySizing.isPossible && !bodySizing.error && !bodySizing.isAvailable && !bodySizing.isLoading;

      return (
        <div key={String(specKey)}>
          <SelectField
            name={selectFieldName}
            showName
            placeholder={t("commons:variantsContentPartialProductDetails.filter.selectFieldNamePlaceholder", {
              fieldName: selectFieldName
            })}
            value={selectFieldValue}
            onChange={handleSelectFieldChange}
            withResetSelectionButton
            onResetSelection={handleResetSelection}
            minContentWidthForMenuItems
            ctaButtons={isFrameSizeKey ? renderCtaButton : undefined}
            icon={isFrameSizeKey && bodySizing.isPossible && renderFrameSizeFieldIcon()}
            onClick={() => setSelectFieldHasBeenOpened(true)}
          >
            {compact([
              isFrameSizeKey && bodySizing.isPossible ? (
                <SelectMenuItem
                  key="__smartfit__"
                  value="smartfit"
                  label={<Image originalSize src={smartfitLogoImage} />}
                  classNames={["u-display-none"]}
                />
              ) : null,
              ...selectMenuItems
            ])}
          </SelectField>
          {isFrameSizeKey && showUnknownSizingError && (
            <Paragraph kind="support" size="s">
              {t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterUnknownError")}
            </Paragraph>
          )}
          {isFrameSizeKey && showNoFittingDataSizingError && (
            <Paragraph kind="support" size="s">
              {t("commons:variantsContentPartialProductDetails.filter.bodySizingFilterNoFittingDataError")}
            </Paragraph>
          )}
        </div>
      );
    });
  };

  const renderAvailabilityStatusCell = (variant: StandaloneProductVariant) => {
    const variantId = variant[ProductKey.VariantId];

    if (wawiProductAvailabilities.query.isFetching || veloconnectProductAvailabilities.query.isFetching) {
      return (
        <DelayedLoadingIndicator size="s" label={t("commons:variantsContentPartialProductDetails.loadingLabel")} />
      );
    }

    const availabilityState = getAvailabilityState(
      wawiProductAvailabilities.getAvailabilityByVariant(variantId),
      veloconnectProductAvailabilities.getAvailabilityByVariant(variantId),
      settings.initialized.customization.availabilityPrioritizationOptions
    );

    return (
      <Availability
        kind={availabilityState.type}
        {...availabilityState}
        label={t(`commons:variantsContentPartialProductDetails.availabilityStatus.${availabilityState.status}`, {
          expectedDeliveryDate: availabilityState.expectedDeliveryDate
            ? formatDate(availabilityState.expectedDeliveryDate, locale)
            : "",
          warehouseLocation: availabilityState.warehouseLocation
        })}
      />
    );
  };

  const renderVariantCustomizationButton = (variantId: ProductId) => {
    if (variantCustomizationLink.isFetching) {
      return (
        <DelayedLoadingIndicator size="s" label={t("commons:variantsContentPartialProductDetails.loadingLabel")} />
      );
    }

    const link = variantCustomizationLink.get(variantId);

    if (link) {
      const isSelected = selectedVariantId === variantId;

      return (
        <Button
          size="s"
          disabled={!hasInternetConnectivity}
          variant={isSelected ? "accent" : "primary"}
          onClick={() => {
            if (isMobileBuild) {
              window.open(link);
            } else {
              history.push(
                ROUTES.BROWSER.INDEX,
                {
                  href: link
                  // BrowserLocationState is commented out, because we aren't allowed to import kiosk types to commons (restricted imports)
                  // This is just temporary, until we have a shared type for kiosk and mobile for the BrowserLocationState
                } /* satisfies BrowserLocationState */
              );
            }
          }}
        >
          {t("commons:variantsContentPartialProductDetails.personalize")}
        </Button>
      );
    }
  };

  const renderCellValue = (specKey: keyof SpecMap[Type], variant: StandaloneProductVariant<Type>) => {
    if (specKey === ProductSpecKey.Price) {
      const pricePartialProps = {
        productOrVariant: variant,
        customization: settings.initialized.customization,
        currency: settings.initialized.currency,
        assortmentPriceSettings: settings.assortmentPriceSettings,
        specFormatter
      };

      return (
        <PriceInfo
          price={<PricePartial {...pricePartialProps} inSpecView />}
          originalPrice={<OriginalPricePartial {...pricePartialProps} inSpecView />}
          badge={<SaleBadgePartial {...pricePartialProps} size="inherit" />}
        />
      );
    }

    // We keep || for the case of empty strings
    return (
      specFormatter?.formatVariantValue(specKey, variant) ||
      t("commons:variantsContentPartialProductDetails.table.noInformationLabel")
    );
  };

  const renderTableHeader = () => {
    return compact([
      <TableHeaderCell key="__selection__">
        {t("commons:variantsContentPartialProductDetails.table.selectionLabel")}
      </TableHeaderCell>,
      ...visibleVariantsTableSpecKeys.map(specKey => (
        <TableHeaderCell key={String(specKey)}>{specFormatter?.formatLabel(specKey)}</TableHeaderCell>
      )),
      showAvailabilityStatus && (
        <TableHeaderCell key="__status__">
          {t("commons:variantsContentPartialProductDetails.table.statusLabel")}
        </TableHeaderCell>
      ),
      isVariantCustomizationButtonEnabled && variantCustomizationLink.showTableHeader && (
        <TableHeaderCell key="__personalize__">
          {t("commons:variantsContentPartialProductDetails.personalize")}
        </TableHeaderCell>
      )
    ]);
  };

  const renderTableRow = (variant: StandaloneProductVariant<Type>) => {
    const variantId = variant[ProductKey.VariantId];

    return (
      <TableRow
        key={variantId}
        ref={variantId === selectedVariantId ? selectedRowRef : undefined}
        selected={variantId === selectedVariantId}
        onClick={() => setSelectedVariantId(variantId)}
      >
        <TableCell verticalPadding="s">
          <RadioField checked={variantId === selectedVariantId} />
        </TableCell>
        {visibleVariantsTableSpecKeys.map(specKey => (
          <TableCell
            icon={
              specKey === BicycleSpecKey.FrameSize &&
              activeBodySizingFilterValues &&
              bodySizing.result && (
                <SizingStatusIcon
                  errorTooltipContent={
                    <>{t("commons:variantsContentPartialProductDetails.table.sizingNotFoundErrorTooltip")}</>
                  }
                  status={bodySizing.result[variantId] === SizingResult.Fit ? "fit" : "error"}
                />
              )
            }
            key={String(specKey)}
            verticalPadding="s"
          >
            {renderCellValue(specKey, variant)}
          </TableCell>
        ))}
        {showAvailabilityStatus && <TableCell verticalPadding="s">{renderAvailabilityStatusCell(variant)}</TableCell>}
        {isVariantCustomizationButtonEnabled && variantCustomizationLink.showButton && (
          <TableCell verticalPadding="s">{renderVariantCustomizationButton(variantId)}</TableCell>
        )}
      </TableRow>
    );
  };

  const headline =
    (veloconnectEndpoint.query.data?.isConfigurable && veloconnectEndpoint.shouldShowNotification) ||
    (veloconnectEndpoint.isImportReady && veloconnectEndpoint.query.data?.isConfigured) ||
    wawiProductAvailabilities.isActive
      ? t("commons:variantsContentPartialProductDetails.variantsAvailabilityHeadline")
      : t("commons:variantsContentPartialProductDetails.variantsHeadline");

  const failedAvailabilityQueries = [
    { source: "local" as const, enabled: wawiProductAvailabilities.isActive, query: wawiProductAvailabilities.query },
    {
      source: "supplier" as const,
      enabled: !!veloconnectEndpoint.query.data?.isConfigured,
      query: veloconnectProductAvailabilities.query
    }
  ].filter(({ enabled, query }) => enabled && query.isError);

  const renderAvailabilityRefreshWarnings = () =>
    failedAvailabilityQueries.length === 0
      ? null
      : failedAvailabilityQueries.map(({ source, query }) => (
          <Note
            key={source}
            kind="warning"
            spacing="s"
            verticalAlignment="center"
            footer={
              <Button size="s" onClick={() => void query.refetch()}>
                {t("commons:variantsContentPartialProductDetails.retryAvailability")}
              </Button>
            }
          >
            {query.dataUpdatedAt
              ? t(`commons:variantsContentPartialProductDetails.availabilityRefreshFailed.${source}`, {
                  lastFetched: formatDateTime(new Date(query.dataUpdatedAt), locale)
                })
              : t(`commons:variantsContentPartialProductDetails.availabilityUnavailable.${source}`)}
          </Note>
        ));

  const toolbarElements: React.JSX.Element[] = [];

  if (
    showAvailabilityStatus &&
    settings.initialized.assortment.type === AssortmentType.Automatic &&
    settings.assortmentFilterSettings.showOnlyAvailableVariants &&
    !settings.assortmentFilterSettings.hideAutomaticAssortmentFilter
  ) {
    toolbarElements.push(
      <FlexLayout autoWidth alignItems="center" gap="l" key="assortment-filter-toggle">
        <StandaloneFilterModal<Type>
          filterKey={ProductSpecKey.CombinedAssortment}
          activeFilters={localActiveFilters.filters}
          onFilterChange={localActiveFilters.setFilters}
          disableDispatchOnAvailabilityLocationChange
        >
          {({ open }) => {
            return (
              <Button size="s" onClick={open}>
                {t("commons:variantsContentPartialProductDetails.availabilityFilterToggleLabel")}
              </Button>
            );
          }}
        </StandaloneFilterModal>
      </FlexLayout>
    );
  }

  return (
    <AvailabilityLayout
      headline={headline}
      noSeparationShadow={noSeparationShadow}
      noTopSpacing={noTopSpacing}
      scrollObserverDeps={[filteredVariants.length, visibleVariantsTableSpecKeys.length]}
      filters={renderVariantFilters()}
      notice={renderAvailabilityRefreshWarnings()}
      toolbar={
        toolbarElements.length === 0 ? null : (
          <FlexLayout gap="l" alignItems="center" justifyContent="center">
            {toolbarElements}
          </FlexLayout>
        )
      }
      selectedVariant={
        selectedVariant && (
          <VariantInformationBoxPartial
            hasInternetConnectivity={hasInternetConnectivity}
            product={product}
            productVariant={selectedVariant}
            getPrintItem={getPrintItem}
            isVeloconnectActive={!!veloconnectEndpoint.query.data?.isConfigured}
            veloconnectAvailabilities={
              !!veloconnectEndpoint.query.data?.isConfigured ? veloconnectProductAvailabilities : undefined
            }
            veloconnectError={veloconnectEndpoint.error ?? veloconnectProductAvailabilities.error}
            isWawiActive={wawiProductAvailabilities.isActive}
            wawiProductAvailabilities={wawiProductAvailabilities.isActive ? wawiProductAvailabilities.query : undefined}
            wawiError={wawiProductAvailabilities.error}
          />
        )
      }
    >
      {productQuery.isFetching && <LoadingResults isMobileBuild={isMobileBuild} />}

      {!productQuery.isFetching && filteredVariants.length === 0 && (
        <EmptyResultsNote
          isMobileBuild={isMobileBuild}
          isSmallViewport={isSmallViewport}
          onClick={localActiveFilters.clearFilters}
        />
      )}

      {productQuery.data && filteredVariants.length > 0 && (
        <Table dynamicSize inContent header={renderTableHeader()}>
          {filteredVariants.map(variant => renderTableRow(variant))}
        </Table>
      )}
    </AvailabilityLayout>
  );
};

export default VariantsContentPartial;
